import React, { useEffect, useRef, useState } from "react";
import { PageLoader } from "@picklebet/ui";
import {
  type VerificationOutcome,
  useOnTransitionedVerificationStatus,
} from "@/hooks/useOnTransitionedVerificationStatus";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { CustomerLicenceOptions } from "../../stores/authStore";
import { telemetry } from "@/utilities/telemetry";
import {
  useCustomerLicence,
  useProfile,
} from "@/hooks/firestore/user/useProfile";
import { navigate } from "utilities/navigate";
import { DEPOSIT_MODAL_PARAM } from "features/wallet/components/deposit/utils/params";
import { useWalletPermissionKey } from "@/features/wallet/hooks/useWalletPermission";
import { useVerifiedPermissions } from "../../hooks/useVerifiedPermissions";

const Verification = ({ promotionCode }: { promotionCode?: string }) => {
  const [attemptedVerification, setAttemptedVerification] =
    useState<VerificationOutcome>("unknown");
  const { profile } = useProfile();
  const licence = useCustomerLicence();
  const isIOM = licence === CustomerLicenceOptions.ISLE_OF_MAN;
  const setModal = useShellStore((state) => state.setModal);
  const depositKey = useWalletPermissionKey("deposit");
  // don't land on the wallet page with stale betting or deposit permissions
  const { isSettling } = useVerifiedPermissions(["submitBet", depositKey]);
  const hasNavigatedRef = useRef(false);

  useOnTransitionedVerificationStatus((outcome) => {
    const payload = {
      returningNSER: profile?.returningNSER ?? false,
      country: profile?.country ?? "",
      state: profile?.state ?? "",
      licence: licence ?? undefined,
      promotionCode: promotionCode ?? "",
    };
    const result = outcome === "passed" ? "success" : "failure";

    telemetry.attemptedVerification({
      result,
      ...payload,
    });
    setAttemptedVerification(outcome === "passed" ? "passed" : "failed");
  });

  useEffect(() => {
    (async () => {
      if (isIOM) {
        setModal(null);
        return;
      }

      if (attemptedVerification === "failed") {
        // once we've attempted verification, navigate to the verification page to show a final status
        navigate("/account/verification");
        setTimeout(() => setModal(null), 1000);
      } else if (
        attemptedVerification === "passed" &&
        !isSettling &&
        !hasNavigatedRef.current
      ) {
        hasNavigatedRef.current = true;
        navigate(`/wallet/overview?${DEPOSIT_MODAL_PARAM}=true`);
      }
    })();
  }, [attemptedVerification, isIOM, setModal, isSettling]);

  return (
    <>
      <PageLoader
        text={
          !isIOM
            ? attemptedVerification === "passed"
              ? "Verified!"
              : attemptedVerification === "failed"
                ? "Verification failed."
                : "Verifying your identity..."
            : "Getting you set up..."
        }
      />
    </>
  );
};

export { Verification };
